import type { IControlOption } from '@/Interfaces/ControlOption.interface';
import CreateItemIcon from '@/components/Icons/CreateItemIcon';
import ModalBody from '@/components/Modal/common/ModalBody';
import ModalTitleCard from '@/components/Modal/common/ModalTitleCard';
import { getAdvertisersFromClients, getAdvertisersFromWhitelabels } from '@/helpers/advertisers';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { selectSharedApiClients, selectSharedApiWhitelabels } from '@/store/selectors/sharedApi.selectors';
import { sendRequest } from '@/store/slices/support.slice';
import { Autocomplete, Button, FormLabel, Modal, TextField, TextareaAutosize } from '@mui/material';
import { type ChangeEvent, type SyntheticEvent, useCallback, useMemo, useState } from 'react';
import { toast } from 'react-toastify';

interface IProps {
  title: string;
  isOpen: boolean;
  handleClose: () => void;
  includeAdvertiser?: boolean;
}

const SupportRequestModal = ({ title = 'Data', isOpen, handleClose, includeAdvertiser }: IProps) => {
  const dispatch = useAppDispatch();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [requestBody, setRequestBody] = useState<string>('');
  const [advertiser, setAdvertiser] = useState<IControlOption | null>(null);

  const { clients } = useAppSelector(selectSharedApiClients);
  const { whitelabels } = useAppSelector(selectSharedApiWhitelabels);

  const advertiserOptions = useMemo<IControlOption[]>(
    () =>
      (whitelabels?.length ? getAdvertisersFromWhitelabels(whitelabels) : getAdvertisersFromClients(clients))?.sort(
        (a, b) => (a?.label ?? '').localeCompare(b?.label ?? ''),
      ),
    [whitelabels, clients],
  );

  const onClose = useCallback(() => {
    setRequestBody('');
    setAdvertiser(null);
    setIsLoading(false);
    handleClose();
  }, [handleClose]);

  const handleInput = useCallback(({ target: { value } }: ChangeEvent<HTMLTextAreaElement>) => {
    setRequestBody(value);
  }, []);

  const handleSendRequest = async () => {
    setIsLoading(true);
    try {
      await dispatch(
        sendRequest({ emailTitle: title, emailBody: requestBody, advertiserId: advertiser?.value }),
      ).unwrap();
      toast.success(`${title} request sent`);
      onClose();
    } catch (error) {
      toast.error((error as Error).message || 'Something went wrong');
      setIsLoading(false);
    }
  };

  const onAdvertiserChange = useCallback((_event: SyntheticEvent, data: IControlOption | null) => {
    setAdvertiser(data);
  }, []);

  return (
    <Modal open={isOpen}>
      <ModalBody>
        <div className='flex flex-col gap-4'>
          <ModalTitleCard
            titleIcon={<CreateItemIcon />}
            handleClose={onClose}
            title={`Request ${title}`}
            subtitle={`You can send a request to the support team to add your desired ${title.toLocaleLowerCase()}.`}
          />
          {includeAdvertiser && (
            <div>
              <FormLabel htmlFor='support-modal-advertiser'>Advertiser*</FormLabel>
              <Autocomplete
                id='support-modal-advertiser'
                fullWidth
                value={advertiser!}
                onChange={onAdvertiserChange}
                getOptionLabel={option => option?.label || ''}
                disableClearable
                isOptionEqualToValue={(option, selectValue) => selectValue && option.value === selectValue?.value}
                getOptionKey={option => option.value}
                options={advertiserOptions}
                renderInput={params => (
                  <TextField
                    {...params}
                    placeholder='Select Advertiser'
                    variant='outlined'
                    error={!advertiser}
                    helperText={!advertiser ? 'Please select an advertiser' : null}
                  />
                )}
              />
            </div>
          )}
          <TextareaAutosize
            className='outline-none border border-[#1B4332] rounded-md p-2.5 resize-none'
            minRows={8}
            maxRows={8}
            placeholder={`Enter ${title} you want to request`}
            onChange={handleInput}
          />
          <Button
            loading={isLoading}
            disabled={!requestBody.length || (includeAdvertiser && !advertiser)}
            variant='contained'
            className='bg-[#1B4332]'
            sx={{
              height: '48px',
              width: '180px',
              marginLeft: 'auto',
              '& .MuiButton-loadingIndicator': {
                position: 'relative',
                left: '12px',
              },
            }}
            onClick={handleSendRequest}
            loadingPosition='end'
          >
            Send{isLoading ? 'ing' : ''} Request
          </Button>
        </div>
      </ModalBody>
    </Modal>
  );
};

export default SupportRequestModal;
